<script setup lang="ts">
import { watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTabController } from '@/composables/useTabController'
import { quoteBlocked } from '@/composables/quoteDisplaySignal'
import { isCustomerSite } from '@/formEnvironment'

const props = defineProps<{
  tabController: Array<{ id: string, name: string, disabled: boolean }>,
  defaultTab?: string,
  // Optional coarse progress tracker: each stage groups one or more step ids under a
  // single label. When provided, the tracker shows stages instead of one dot per step.
  trackerStages?: Array<{ label: string, steps: string[] }>,
  // sessionStorage key for resume-on-reload (Factory decides the starting step + passes this).
  persistKey?: string
}>()

const { currentTabId, show, goNext, goBack, goLast, allItems } =
  useTabController(props.defaultTab ?? 'nav-initial-tab', JSON.parse(JSON.stringify(props.tabController)), props.persistKey)
let copiedTabController = JSON.parse(JSON.stringify(props.tabController ?? []))

watch(
  allItems,
  (newVal) => {
    copiedTabController = JSON.parse(JSON.stringify(newVal))
  },
  { immediate: true, deep: true }
)


  defineExpose({
    goBack,
    goNext,
    goLast,
    show,
    currentTabId
  })

// Reflect each step as a URL path segment (/retail-shops/quote/<step>) for clean GA paths + back/forward.
// Customersite only — backoffice embeds the journey inside Atlas's own routing.
if (isCustomerSite) {
  const router = useRouter()
  const route = useRoute()
  let syncedOnce = false
  watch(currentTabId, (id) => {
    if (!id || route.params.step === id) return
    const path = `/retail-shops/quote/${id}`
    // First sync replaces (no stray stepless history entry); later steps push so back/forward work.
    if (syncedOnce) { router.push(path) } else { router.replace(path); syncedOnce = true }
  }, { immediate: true })
  watch(() => route.params.step, (step) => {
    if (typeof step === 'string' && step !== currentTabId.value && allItems.some((t) => t.id === step)) show(step)
  })
}

// Backward-only: ignore clicks on the current or a later step.
function navigateBackTo(id: string) {
  const targetIndex = allItems.findIndex((t) => t.id === id)
  const currentIndex = allItems.findIndex((t) => t.id === currentTabId.value)
  if (targetIndex === -1 || targetIndex > currentIndex) return
  show(id)
}

function onTabClick(id: string, e: MouseEvent) {
  e.preventDefault()
  navigateBackTo(id)
}

// A completed stage jumps back to its first reachable step (navigateBackTo no-ops the rest).
function onStageClick(stage: { label: string, steps: string[] }) {
  const targetId = stage.steps.find((s) => allItems.some((t) => t.id === s))
  if (targetId) navigateBackTo(targetId)
}

// The stage that contains the current step (drives active/completed on the coarse tracker).
const activeStageIndex = computed(() =>
  (props.trackerStages ?? []).findIndex((s) => s.steps.includes(currentTabId.value as string))
)

// The page title mirrors the stage, except where a step needs a more specific heading.
const STEP_TITLES: Record<string, string> = { registration: 'Registration', assumptions: 'Additional questions', confirmation: 'Your confirmed quotation' }
const pageTitle = computed(() =>
  // A blocked (referred/declined) quote shows its own message card, so drop the page title.
  quoteBlocked.value ? ''
    : (STEP_TITLES[currentTabId.value as string]
        ?? (props.trackerStages?.[activeStageIndex.value] ?? props.trackerStages?.[0])?.label
        ?? ''))
</script>

<template>
  <div class="quote-container">
    <nav>
      <!-- Off-white page title above the form card = the current step's heading (stage by default). -->
      <Teleport to="#md-page-title" v-if="trackerStages?.length">
        {{ pageTitle }}
      </Teleport>

      <!-- Coarse Markel Direct tracker: one dot per journey stage, teleported into the header strip. -->
      <Teleport to="#md-stepper" v-if="trackerStages?.length">
        <ul class="step-menu" role="tablist">
          <li
            v-for="(stage, index) in trackerStages"
            :key="stage.label"
            class="step-item"
            :class="{ active: index === activeStageIndex, completed: activeStageIndex > -1 && index < activeStageIndex }"
            role="tab"
            :aria-selected="index === activeStageIndex"
            :tabindex="activeStageIndex > -1 && index < activeStageIndex ? 0 : -1"
            @click="onStageClick(stage)"
            @keydown.enter.prevent="onStageClick(stage)"
            @keydown.space.prevent="onStageClick(stage)">
              <div class="step-dot">
                  <span class="index">{{ index + 1 }}</span>
                  <span class="check"/>
              </div>
              <div class="step-label">{{ stage.label }}</div>
          </li>
        </ul>
      </Teleport>

      <!-- Default: one dot per step, driven by the tab controller. -->
      <ul v-else class="step-menu" id="nav-tab" role="tablist">
        <template v-for="(tab, index) in copiedTabController" :key="tab.id">
          <li
            v-if="!tab.hide"
            :id="tab.id"
            class="step-item"
            :class="{ active: currentTabId === tab.id, disabled: tab.disabled, completed: allItems.findIndex((t) => t.id === tab.id) < allItems.findIndex((t) => t.id === currentTabId) }"
            :href="`#${tab.id}`"
            role="tab"
            :aria-controls="tab.id"
            :aria-selected="currentTabId === tab.id"
            @click="onTabClick(tab.id as string, $event)">
              <div class="step-dot">
                  <span class="index">{{ (index as number) + 1 }}</span>
                  <span class="check"/>
                  <span class="edit fa-solid fa-pencil"/>
              </div>
              <div class="step-label">{{ tab.name }}</div>
          </li>
        </template>
      </ul>
    </nav>

    <div class="tab-content" id="nav-tabContent">
      <template v-for="tab in copiedTabController" :key="tab.id">
        <div
          v-if="!tab.hide"
          :id="tab.id"
          class="tab-pane fade"
          :class="{ 'show active': currentTabId === tab.id }"
          role="tabpanel"
          :aria-labelledby="tab.id"
        >
          <slot :name="tab.id"/>
        </div>
      </template>
    </div>
  </div>
</template>
